import { RegisteredComponent } from "@builder.io/sdk-react";
import { DLD_FIELD, DLT_FIELD } from "../commonFields";

export const interactiveTableStepsBlockConfig: Omit<
  RegisteredComponent,
  "component"
> = {
  name: "Interactive Table Steps",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderContext: true,
    builderBlock: true,
    builderComponents: true,
    builderLinkComponent: true,
  },
  canHaveChildren: true,
  childRequirements: {
    message:
      "This component expects a registration form (e.g. Builder 'Form' block) as its child.",
  },
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: "Text",
        options: {
          text: "Drop your registration form here",
        },
      },
    },
  ],
  inputs: [
    /** Steps reorder requested in WEBDEV-53722*/
    {
      name: "step2Title",
      friendlyName: "Step 1 Title",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Step 1",
      },
    },
    {
      name: "step2Description",
      friendlyName: "Step 1 Description",
      type: "richText",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "This is a pro feature {{star}} or Task completed {{check}}",
      },
      helperText:
        "You can use the {{star}} and {{check}} syntax to insert icons into the rich text.",
    },

    {
      name: "step3Title",
      friendlyName: "Step 2 Title",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Step 2",
      },
    },
    {
      name: "step3Description",
      friendlyName: "Step 2 Description",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default:
          "Optional: Use the dropdown to isolate a common challenge you are trying to solve.",
      },
    },
    {
      name: "step3ModalError",
      friendlyName: "Step 2 Modal Error",
      type: "object",
      defaultValue: {
        header: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Registration Error",
        },
        description: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "There was an issue with your submission.",
        },
      },
      subFields: [
        {
          name: "header",
          type: "string",
          localized: true,
          defaultValue: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Registration Error",
          },
        },
        {
          name: "description",
          type: "richText",
          localized: true,
          defaultValue: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "There was an issue with your submission.",
          },
          helperText:
            "You can use the {{star}} and {{check}} syntax to insert icons into the rich text.",
        },
      ],
    },
    {
      name: "step3ButtonText",
      friendlyName: "Step 2 Button Text",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Submit",
      },
    },
    {
      name: "step3UnderReview",
      friendlyName: "Step 2 Under Review",
      type: "object",
      defaultValue: {
        header: "Under Review",
        description: "Your submission is currently under review.",
      },
      subFields: [
        {
          name: "header",
          type: "string",
          localized: true,
          defaultValue: "Under Review",
        },
        {
          name: "description",
          type: "richText",
          localized: true,
          defaultValue: "Your submission is currently under review.",
        },
      ],
    },
    DLT_FIELD,
    DLD_FIELD,
    {
      name: "step1Title",
      friendlyName: "Step 3 Title",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Step 3",
      },
    },
    {
      name: "step1OptionalText",
      friendlyName: "Optional Text",
      type: "string",
      localized: true,
      required: false,
      helperText: "Optional text shown near the Step 3 title.",
    },
    {
      name: "step1Description",
      friendlyName: "Step 3 Description",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default:
          "Optional: Use the dropdown to isolate a common challenge you are trying to solve.",
      },
    },
    {
      name: "removeChallengeCta",
      type: "string",
      localized: true,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Remove all challenges",
      },
      helperText: "The text for the 'clear' option in the dropdown",
    },
    {
      name: "challenges",
      type: "list",
      required: true,
      subFields: [
        {
          name: "key",
          type: "string",
          required: true,
          defaultValue: "option-1",
          helperText: "Internal challenge key",
        },
        {
          name: "value",
          type: "string",
          required: true,
          localized: true,
          defaultValue: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Option 1",
          },
          helperText: "Visible label",
        },
        {
          name: "analytics",
          type: "object",
          required: false,
          subFields: [DLT_FIELD, DLD_FIELD],
        },
      ],
      defaultValue: [
        {
          key: "option-1",
          value: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Option 1",
          },
        },
        {
          key: "option-2",
          value: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Option 2",
          },
        },
      ],
    },
    {
      name: "selectedChallenge",
      type: "object",
      localized: false,
      defaultValue: "{{state.selectedChallenge}}",
      helperText:
        "The selected challenge. If not set, the placeholder will be shown",
      subFields: [
        {
          name: "key",
          type: "string",
        },
        {
          name: "value",
          type: "string",
        },
      ],
    },
    {
      name: "onChallengeChange",
      type: "javascript",
      localized: false,
    },
    {
      name: "placeholder",
      type: "string",
      localized: true,
      required: false,
      defaultValue: {
        "@type": "@builder.io/core:LocalizedValue",
        Default: "Select a Challenge",
      },
    },
  ],
};
